import React, { useState } from 'react';
import { Loader2, Reply, X } from 'lucide-react';

const COMMENT_PAGE_SIZE = 10;

const CommentsWidget = ({
    comments = [],
    onAddComment,
    isCommenting
}) => {
    const [newComment, setNewComment] = useState("");
    const [commentPage, setCommentPage] = useState(1);
    const [replyingTo, setReplyingTo] = useState(null); // { id, text, author }

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        // Pass reply context to Dashboard handler
        if (onAddComment) {
            onAddComment(e, newComment, replyingTo);
        }
        setNewComment("");
        setReplyingTo(null);
    };

    const handleReply = (comment) => {
        setReplyingTo({
            id: comment.id || comment._id,
            text: comment.text,
            author: comment.author || 'Unknown'
        });
    };

    const cancelReply = () => {
        setReplyingTo(null);
    };

    // Truncate text for reply preview
    const truncateText = (text, maxLength = 100) => {
        if (!text) return '';
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const sortedComments = [...comments].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || a.timestamp || 0);
        const dateB = new Date(b.createdAt || b.created_at || b.timestamp || 0);
        return dateB.getTime() - dateA.getTime();
    });

    const displayComments = sortedComments.slice(0, commentPage * COMMENT_PAGE_SIZE);
    const hasMore = comments.length > commentPage * COMMENT_PAGE_SIZE;

    // Find parent comment for a reply
    const findParentComment = (replyToId) => {
        return comments.find(c => (c.id || c._id) === replyToId);
    };

    return (
        <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-5 bg-white dark:bg-slate-900/70">
            <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Comments</p>
                <span className="text-xs text-slate-500">
                    {comments.length} entr{comments.length === 1 ? "y" : "ies"}
                </span>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
                {/* Reply preview bar - shows when replying to a comment */}
                {replyingTo && (
                    <div className="flex items-start gap-2 p-2 bg-sky-50 dark:bg-sky-900/20 border-l-4 border-sky-500 rounded-r-lg">
                        <Reply className="w-4 h-4 text-sky-500 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-sky-600 dark:text-sky-400">
                                Replying to {replyingTo.author}
                            </p>
                            <p className="text-xs text-slate-600 dark:text-slate-400 truncate">
                                {truncateText(replyingTo.text, 80)}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={cancelReply}
                            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <textarea
                    value={newComment}
                    onChange={(event) => setNewComment(event.target.value)}
                    rows={3}
                    placeholder={replyingTo ? `Reply to ${replyingTo.author}...` : "Add a note about conditions, follow-ups, or next steps..."}
                    className="w-full bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-3 py-2 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:border-sky-500 focus:outline-none text-sm"
                    autoFocus={!!replyingTo}
                />
                <div className="flex justify-end gap-2">
                    {replyingTo && (
                        <button
                            type="button"
                            onClick={cancelReply}
                            className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        type="submit"
                        className="px-4 py-2 rounded-lg bg-sky-600 text-white font-semibold hover:bg-sky-500 transition-colors disabled:opacity-60 flex items-center gap-2"
                        disabled={!newComment.trim() || isCommenting}
                    >
                        {isCommenting && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isCommenting ? "Posting..." : replyingTo ? "Reply" : "Add Comment"}
                    </button>
                </div>
            </form>
            <div className="mt-4 space-y-2">
                {comments.length > 0 ? (
                    <>
                        {displayComments.map((comment, index) => {
                            const parentComment = comment.replyToId ? findParentComment(comment.replyToId) : null;

                            return (
                                <div key={`${comment.createdAt || comment.created_at || index}-${index}`} className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2">
                                    {/* Threaded reply context - shows quoted parent message */}
                                    {(comment.replyToId || comment.replyToText) && (
                                        <div className="mb-2 pl-2 border-l-2 border-slate-300 dark:border-slate-700">
                                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                                <span className="font-medium">
                                                    {comment.replyToAuthor || parentComment?.author || 'Unknown'}
                                                </span>
                                            </p>
                                            <p className="text-xs text-slate-400 dark:text-slate-500 line-clamp-2">
                                                {comment.replyToText || parentComment?.text || ''}
                                            </p>
                                        </div>
                                    )}
                                    <p className="text-slate-700 dark:text-slate-200 text-sm">{comment.text}</p>
                                    <div className="flex items-center justify-between mt-1">
                                        <p className="text-xs text-slate-500">
                                            {comment.author || 'Unknown'} • {(() => {
                                                const dateStr = comment.createdAt || comment.created_at || comment.timestamp;
                                                if (!dateStr) return 'Just now';
                                                const date = new Date(dateStr);
                                                return isNaN(date.getTime()) ? 'Just now' : date.toLocaleString();
                                            })()}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => handleReply(comment)}
                                            className="text-xs text-slate-400 hover:text-sky-500 dark:hover:text-sky-400 flex items-center gap-1 transition-colors"
                                        >
                                            <Reply className="w-3 h-3" />
                                            Reply
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                        {hasMore && (
                            <div className="flex justify-center">
                                <button
                                    onClick={() => setCommentPage((prev) => prev + 1)}
                                    className="px-3 py-1 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs hover:border-sky-500 transition-colors"
                                >
                                    Show more comments
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <p className="text-slate-500 text-sm">No comments yet.</p>
                )}
            </div>
        </div>
    );
};
export default CommentsWidget;
